import React, { useState, useRef, useCallback } from 'react';
import useInvisibleRecaptcha from '../../hooks/useInvisibleRecaptcha';
import { CheckIcon } from '../icons';
import AwningFields from './quote-fields/AwningFields';
import PergolaFields from './quote-fields/PergolaFields';
import { apiFetch } from '../../services/api';
import { trackQuoteRequest } from '../../services/googleAds';
import { RECAPTCHA_SITE_KEY } from '../../config/env';
import { WARRANTY_YEARS } from '../../config/warranty';
import { AWNING_FROM_LABEL } from '../../config/startingPrice';
import { STOCK_SHORT, LEAD_PERGOLA } from '../../config/leadTimes';

// QuoteFormSection — the stepped "request a quote" form on the
// homepage. Largest single section by far. Self-contained: owns
// its own category-select / submit / recaptcha state so HomePage
// doesn't need to know anything about the form internals.
//
// Step 1 picks a product category (awning / pergola); step 2 swaps
// category-specific fields above the common contact
// fields, plus a "Change" pill to go back. POSTs to /api/quotes
// after invisible reCAPTCHA verification.
export default function QuoteFormSection() {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [quoteCategory, setQuoteCategory] = useState('');

  const formRef = useRef(null);

  const submitForm = useCallback(async (recaptchaToken) => {
    const form = formRef.current;
    if (!form) return;

    const formData = new FormData(form);
    const data = {};
    formData.forEach((value, key) => {
      if (key === 'attachments[]') {
        if (!data.attachments) data.attachments = [];
        data.attachments.push(value);
      } else {
        data[key] = value;
      }
    });

    if (recaptchaToken) {
      data.recaptchaToken = recaptchaToken;
    }

    try {
      const response = await apiFetch('/api/quotes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        // Same conversion as the /request-quote page: a quote request the
        // server accepted.
        trackQuoteRequest({ email: data.email, phone: data.phone });
        setSubmitSuccess('Thank you! Your quote request has been submitted. We\'ll be in touch soon.');
        form.reset();
      } else {
        const err = await response.json().catch(() => ({}));
        setSubmitError(err.error || 'Something went wrong. Please try again or contact us directly.');
      }
    } catch {
      setSubmitError('Something went wrong. Please try again or contact us directly.');
    }
    recaptchaReset();
    setSubmitting(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reCAPTCHA can fail in ways that never call onVerify - the visitor dismisses
  // the challenge, it expires, or the script is blocked. Without this the
  // button sits on "Submitting..." forever and the lead is lost silently.
  const handleRecaptchaUnavailable = useCallback((reason) => {
    setSubmitting(false);
    setSubmitError(
      reason === 'blocked'
        ? "We couldn't load the spam check - an ad blocker or privacy extension is usually the cause. Please allow it for this page and try again, or email info@asgshading.com.au or call +61 422 439 499 and we'll take your details."
        : reason === 'expired'
          ? "The spam check timed out. Please try again."
          : "The spam check could not be completed. Please try again, or email info@asgshading.com.au or call +61 422 439 499."
    );
  }, []);


  const { containerRef: recaptchaRef, execute: recaptchaExecute, reset: recaptchaReset, prepare: loadRecaptcha } =
    useInvisibleRecaptcha({ siteKey: RECAPTCHA_SITE_KEY, onVerify: submitForm, onUnavailable: handleRecaptchaUnavailable });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');
    setSubmitting(true);
    recaptchaExecute();
  };

  const categoryLabel = quoteCategory === 'awning' ? 'Awning' : 'Pergola';

  return (
    <section className="asg-quote" id="quote">
      <div className="asg-quote-lines"></div>
      <div className="asg-quote-inner">
        <div className="asg-quote-text asg-reveal">
          <p className="asg-section-label asg-section-label--accent">Get Started</p>
          <h2>Request your<br />no-obligation quote.</h2>
          <p>
            Send us a couple of photos and your measurements. We'll advise remotely and
            send a detailed written quote - no site visit, no obligation.
          </p>
          <ul className="asg-quote-perks">
            <li><CheckIcon />Awnings: made to measure, free delivery to most of Melbourne</li>
            <li><CheckIcon />Pergolas: Australia-wide supply</li>
            <li><CheckIcon />Detailed written quote, no obligation</li>
            <li><CheckIcon />{WARRANTY_YEARS}-year warranty*</li>
          </ul>
        </div>
        <form className="asg-quote-form asg-reveal" ref={formRef} onSubmit={handleSubmit} onFocus={loadRecaptcha}>
          {/* STEP 1 — Category select */}
          {!quoteCategory && (
            <div className="asg-form-step">
              <label className="asg-form-step-label">What are you after?</label>
              <div className="asg-cat-grid">
                <button type="button" className="asg-cat-card" onClick={() => setQuoteCategory('awning')}>
                  <span className="asg-cat-card-title">Retractable Awning</span>
                  <span className="asg-cat-card-sub">{AWNING_FROM_LABEL} · Free delivery to most of Melbourne · {STOCK_SHORT}</span>
                </button>
                <button type="button" className="asg-cat-card" onClick={() => setQuoteCategory('pergola')}>
                  <span className="asg-cat-card-title">Aluminium Pergola</span>
                  <span className="asg-cat-card-sub">Supply only · {LEAD_PERGOLA} · You arrange installer</span>
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — Category-specific + common fields */}
          {quoteCategory && (
            <>
              <div className="asg-form-cat-pill">
                <span>For: <strong>{categoryLabel}</strong></span>
                <button type="button" className="asg-form-change" onClick={() => setQuoteCategory('')}>Change</button>
              </div>
              <input type="hidden" name="category" value={quoteCategory} />

              {quoteCategory === 'awning' && <AwningFields />}
              {quoteCategory === 'pergola' && <PergolaFields />}

              {/* COMMON contact fields */}
              <div className="asg-form-divider"><span>Your details</span></div>
              <div className="asg-form-row">
                <input type="text" className="asg-input" name="full_name" placeholder="Full name" required />
                <input type="email" className="asg-input" name="email" placeholder="Email address" required />
              </div>
              <div className="asg-form-row">
                <input type="tel" className="asg-input" name="phone" placeholder="Phone (optional)" />
                <input type="text" className="asg-input" name="suburb" placeholder="Suburb / postcode" />
              </div>
              <textarea
                className="asg-input asg-input-textarea"
                name="message"
                placeholder="Anything else? Tell us about your space, style preferences, or questions..."
              ></textarea>
              {RECAPTCHA_SITE_KEY && <div ref={recaptchaRef}></div>}
              {submitError && <p className="asg-form-error" role="alert">{submitError}</p>}
              {submitSuccess && <p className="asg-form-success" role="status">{submitSuccess}</p>}
              <button type="submit" className="asg-form-submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Request Quote'}
              </button>
            </>
          )}
        </form>
      </div>
    </section>
  );
}
